import { useEffect, useRef, useState } from 'react';
import { FilePlus2, Download, Trash2, CheckCircle2, Paperclip } from 'lucide-react';
import { api, buildQuery } from '../lib/api';
import type { Bill } from '../types';
import { formatDate, formatDateTime, formatFileSize } from '../lib/format';
import {
  Spinner, EmptyState, StatusBadge, ConfirmDialog, Alert,
} from './ui';
import BillFormModal, { BILL_TYPE_LABELS } from './BillFormModal';

interface Props {
  role: string;
}

const kindLabel = (kind: string) =>
  kind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function BillsView({ role }: Props) {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [payingId, setPayingId] = useState<number | null>(null);
  const [payReceipt, setPayReceipt] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const receiptInputRef = useRef<HTMLInputElement>(null);

  const canManage = role === 'admin' || role === 'manager';
  const canCreate = role === 'staff' || role === 'manager';

  const load = () => {
    setLoading(true);
    api
      .get<Bill[]>(`/bills${buildQuery({ status: statusFilter, bill_type: typeFilter })}`)
      .then(setBills)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter, typeFilter]);

  const handlePay = async (id: number) => {
    if (!payReceipt) return;
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('status', 'paid');
      fd.append('paid_receipt', payReceipt);
      await api.patch(`/bills/${id}/status`, fd);
      setPayingId(null);
      setPayReceipt(null);
      if (receiptInputRef.current) receiptInputRef.current.value = '';
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/bills/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Bills</h1>
        {canCreate && (
          <button onClick={() => { setError(''); setModalOpen(true); }} className="px-4 py-2 bg-primary text-black rounded-lg flex items-center space-x-2 hover:bg-primary-dark text-sm">
            <FilePlus2 className="h-4 w-4" /> Submit Bill
          </button>
        )}
      </div>

      {error && <div className="mb-4"><Alert>{error}</Alert></div>}

      <div className="flex gap-3 mb-4 flex-wrap">
        <select
          className="w-44 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          {(Object.keys(BILL_TYPE_LABELS) as (keyof typeof BILL_TYPE_LABELS)[]).map((t) => (
            <option key={t} value={t}>{BILL_TYPE_LABELS[t]}</option>
          ))}
        </select>
        <select
          className="w-40 px-4 py-2 border border-gray-300 rounded-lg text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
      </div>

      {loading ? (
        <Spinner label="Loading bills..." />
      ) : bills.length === 0 ? (
        <EmptyState message="No bills found." />
      ) : (
        <div className="grid gap-4">
          {bills.map((b) => (
            <div key={b.id} className="bg-white p-5 rounded-lg shadow-lg">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <h3 className="font-semibold">{b.title || BILL_TYPE_LABELS[b.bill_type]}</h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{BILL_TYPE_LABELS[b.bill_type]}</span>
                    <StatusBadge status={b.status} />
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Submitted by {b.user_name} · {formatDateTime(b.created_at)}
                    {b.directed_to_name ? ` · Directed to ${b.directed_to_name}` : ''}
                    {b.paid_by_name ? ` · Paid by ${b.paid_by_name}` : ''}
                    {b.paid_at ? ` · ${formatDate(b.paid_at)}` : ''}
                  </p>

                  {(b.liters_supplied !== null || b.amount_per_liter !== null || b.density !== null || b.last_reading !== null || b.amount !== null) && (
                    <div className="flex flex-wrap items-center gap-3 mb-2 text-sm">
                      {b.liters_supplied !== null && <span className="text-gray-700"><span className="font-medium">Liters:</span> {b.liters_supplied} L</span>}
                      {b.amount_per_liter !== null && <span className="text-gray-700"><span className="font-medium">Per liter:</span> ₦{b.amount_per_liter}</span>}
                      {b.density !== null && <span className="text-gray-700"><span className="font-medium">Density:</span> {b.density}</span>}
                      {b.last_reading !== null && <span className="text-gray-700"><span className="font-medium">Last reading:</span> {b.last_reading} kWh</span>}
                      {b.amount !== null && <span className="text-gray-700"><span className="font-medium">Amount:</span> ₦{b.amount.toLocaleString()}</span>}
                    </div>
                  )}

                  {b.description && <p className="text-sm text-gray-600 whitespace-pre-line mb-3">{b.description}</p>}

                  {b.files.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 mb-1 flex items-center"><Paperclip className="h-3 w-3 mr-1" /> Files</p>
                      <div className="flex flex-wrap gap-2">
                        {b.files.map((f) => (
                          <a
                            key={f.id}
                            href={f.url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs hover:bg-gray-200"
                          >
                            <Download className="h-3.5 w-3.5" />
                            <span className="max-w-[180px] truncate">{f.filename}</span>
                            <span className="text-gray-400">{formatFileSize(f.size)}</span>
                            <span className="text-gray-400">· {kindLabel(f.kind)}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="shrink-0 space-y-2">
                  {canManage && b.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <input
                        ref={receiptInputRef}
                        type="file"
                        accept="image/*,.pdf"
                        className="text-xs"
                        onChange={(e) => setPayReceipt(e.target.files?.[0] || null)}
                      />
                      <button
                        onClick={() => payingId === b.id ? handlePay(b.id) : setPayingId(b.id)}
                        disabled={saving || !payReceipt}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg text-xs flex items-center gap-1.5 hover:bg-green-700 disabled:opacity-50"
                        title="Mark as paid and upload receipt"
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> {payingId === b.id && saving ? 'Saving...' : 'Mark Paid'}
                      </button>
                    </div>
                  )}
                  {b.status === 'paid' && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600"><CheckCircle2 className="h-3.5 w-3.5" /> Paid</span>
                  )}
                  {canManage && (
                    <button onClick={() => setDeleteId(b.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete bill">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BillFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={load}
        role={role}
      />

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Bill"
        message="Are you sure you want to delete this bill and all its files?"
      />
    </div>
  );
}
